import { notesApi } from '@/lib/api';
import type {
  FolderNotesResponse,
  MatrixMemo,
  MatrixNotesResponse,
  Note,
  NoteTarget,
  NoteTargetInput,
  NoteThread,
  NoteVisibility,
  UpdateNoteInput,
  UserRole,
} from '@mss_creator/shared';
import {
  type QueryClient,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

/**
 * Queries, mutations and pure helpers of the notes kit (issue #19,
 * `docs/specs/matrices.md` "Notes de matrice"). The server filters every
 * answer by the level of each note, so nothing here hides a note.
 *
 * A write refreshes the notes of its scope and the two lists that carry
 * counters: the company's matrices (`['matrices', companyId]`, card counters
 * and memo preview) and folders (`['folders', companyId]`, folder counter).
 */

// ── The viewer and the scope ─────────────────────────────────────────────────

/**
 * Who is looking: the id tells which notes are theirs to edit, the role which
 * levels they may pick. Pages take both from `useQuery(['me'])` and
 * `useEffectiveRole(me)`, as the matrix and company routes do.
 */
export interface NoteViewer {
  id: string;
  role: UserRole;
}

/** Where discussions live: on a matrix (with its memo) or on a folder. */
export type NotesScope =
  | { type: 'matrix'; matrixId: string }
  | { type: 'folder'; folderId: string };

export const notesKeys = {
  matrix: (matrixId: string) => ['notes', 'matrix', matrixId] as const,
  folder: (folderId: string) => ['notes', 'folder', folderId] as const,
  scope: (scope: NotesScope) =>
    scope.type === 'matrix' ? notesKeys.matrix(scope.matrixId) : notesKeys.folder(scope.folderId),
};

// ── Pure helpers ─────────────────────────────────────────────────────────────

/** A stable string for a target: two notes are on the same spot when their keys are equal. */
export function targetKey(target: NoteTarget): string {
  switch (target.type) {
    case 'matrix':
      return 'matrix';
    case 'folder':
      return 'folder';
    case 'column':
      return `column:${target.columnNumber}`;
    case 'row':
      return `row:${target.rowType}:${target.rowRef}`;
    case 'cell':
      return `cell:${target.rowType}:${target.rowRef}:${target.columnNumber}`;
  }
}

export function isSameTarget(a: NoteTarget, b: NoteTarget): boolean {
  return targetKey(a) === targetKey(b);
}

/** The discussions (open and resolved) written on `target`, in the order given. */
export function threadsForTarget(threads: NoteThread[], target: NoteTarget): NoteThread[] {
  const key = targetKey(target);
  return threads.filter((thread) => targetKey(thread.target) === key);
}

export function isThreadResolved(thread: NoteThread): boolean {
  return thread.resolvedAt !== null;
}

/**
 * Open discussions per target key: what the grid's markers show. Resolved
 * discussions count nowhere. A note whose dimension is gone comes back as a
 * matrix note and counts on the matrix.
 */
export function openCountsByTarget(threads: NoteThread[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const thread of threads) {
    if (isThreadResolved(thread)) continue;
    const key = targetKey(thread.target);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

// ── Queries ──────────────────────────────────────────────────────────────────

/** The memo and the visible discussions of a matrix. Idle while `matrixId` is undefined. */
export function useMatrixNotes(matrixId: string | undefined): UseQueryResult<MatrixNotesResponse> {
  return useQuery({
    queryKey: notesKeys.matrix(matrixId ?? ''),
    queryFn: () => notesApi.listForMatrix(matrixId ?? ''),
    enabled: matrixId !== undefined,
  });
}

/** The visible discussions of a folder. Idle while `folderId` is undefined. */
export function useFolderNotes(folderId: string | undefined): UseQueryResult<FolderNotesResponse> {
  return useQuery({
    queryKey: notesKeys.folder(folderId ?? ''),
    queryFn: () => notesApi.listForFolder(folderId ?? ''),
    enabled: folderId !== undefined,
  });
}

export interface ScopeNotes {
  threads: NoteThread[];
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/** The discussions of a scope, whichever it is (one hook for the panel shared by matrix and folder). */
export function useScopeNotes(scope: NotesScope): ScopeNotes {
  const matrixQuery = useMatrixNotes(scope.type === 'matrix' ? scope.matrixId : undefined);
  const folderQuery = useFolderNotes(scope.type === 'folder' ? scope.folderId : undefined);
  const query = scope.type === 'matrix' ? matrixQuery : folderQuery;
  return {
    threads: query.data?.threads ?? [],
    isPending: query.isPending,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

// ── Mutations ────────────────────────────────────────────────────────────────

function invalidateNotes(queryClient: QueryClient, scope: NotesScope, companyId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: notesKeys.scope(scope) }),
    queryClient.invalidateQueries({ queryKey: ['matrices', companyId] }),
    queryClient.invalidateQueries({ queryKey: ['folders', companyId] }),
  ]);
}

export interface CreateNoteVariables {
  /** The target of a matrix note (the matrix itself when omitted); ignored for a folder. */
  target?: NoteTargetInput;
  body: string;
  visibility: NoteVisibility;
}

/** A new discussion on the scope. */
export function useCreateNote(scope: NotesScope, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ target, body, visibility }: CreateNoteVariables): Promise<NoteThread> =>
      scope.type === 'matrix'
        ? notesApi.createForMatrix(scope.matrixId, {
            target: target ?? { type: 'matrix' },
            body,
            visibility,
          })
        : notesApi.createForFolder(scope.folderId, { body, visibility }),
    onSuccess: () => invalidateNotes(queryClient, scope, companyId),
  });
}

/** An answer to a root note; it takes the level of its root. */
export function useReplyToNote(scope: NotesScope, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, body }: { noteId: string; body: string }): Promise<Note> =>
      notesApi.reply(noteId, { body }),
    onSuccess: () => invalidateNotes(queryClient, scope, companyId),
  });
}

/** The author edits the text, and the level of a root note. */
export function useUpdateNote(scope: NotesScope, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, input }: { noteId: string; input: UpdateNoteInput }): Promise<Note> =>
      notesApi.update(noteId, input),
    onSuccess: () => invalidateNotes(queryClient, scope, companyId),
  });
}

/** The author deletes a note; deleting a root note deletes its discussion. */
export function useDeleteNote(scope: NotesScope, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string): Promise<void> => notesApi.remove(noteId),
    onSuccess: () => invalidateNotes(queryClient, scope, companyId),
  });
}

export function useResolveNote(scope: NotesScope, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string): Promise<NoteThread> => notesApi.resolve(noteId),
    onSuccess: () => invalidateNotes(queryClient, scope, companyId),
  });
}

export function useReopenNote(scope: NotesScope, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string): Promise<NoteThread> => notesApi.reopen(noteId),
    onSuccess: () => invalidateNotes(queryClient, scope, companyId),
  });
}

/**
 * Writes the memo of a matrix. `baseUpdatedAt` is the `memo.updatedAt` read
 * before editing: a memo changed since answers 409 `MEMO_CONFLICT`. On success
 * the cached memo is replaced right away, then the lists refresh.
 */
export function useUpdateMemo(matrixId: string, companyId: string) {
  const queryClient = useQueryClient();
  const scope: NotesScope = { type: 'matrix', matrixId };
  return useMutation({
    mutationFn: ({
      text,
      baseUpdatedAt,
    }: { text: string; baseUpdatedAt: string | null }): Promise<MatrixMemo> =>
      notesApi.updateMemo(matrixId, { text, baseUpdatedAt }),
    onSuccess: (memo) => {
      queryClient.setQueryData<MatrixNotesResponse>(notesKeys.matrix(matrixId), (previous) =>
        previous ? { ...previous, memo } : previous,
      );
      return invalidateNotes(queryClient, scope, companyId);
    },
  });
}

/** What a discussion needs to be acted on: each call resolves once the server answered. */
export interface NoteThreadActions {
  reply: (noteId: string, body: string) => Promise<Note>;
  update: (noteId: string, input: UpdateNoteInput) => Promise<Note>;
  remove: (noteId: string) => Promise<void>;
  resolve: (noteId: string) => Promise<NoteThread>;
  reopen: (noteId: string) => Promise<NoteThread>;
}

/**
 * The actions of a discussion for a scope, ready to hand to `NoteThread`
 * (one call in a page, however many discussions it lists).
 */
export function useNoteActions(scope: NotesScope, companyId: string): NoteThreadActions {
  const reply = useReplyToNote(scope, companyId);
  const update = useUpdateNote(scope, companyId);
  const remove = useDeleteNote(scope, companyId);
  const resolve = useResolveNote(scope, companyId);
  const reopen = useReopenNote(scope, companyId);
  return {
    reply: (noteId, body) => reply.mutateAsync({ noteId, body }),
    update: (noteId, input) => update.mutateAsync({ noteId, input }),
    remove: (noteId) => remove.mutateAsync(noteId),
    resolve: (noteId) => resolve.mutateAsync(noteId),
    reopen: (noteId) => reopen.mutateAsync(noteId),
  };
}
